import { useEffect, useRef, useState } from 'react';
import { apiJson, isUnauthorizedError } from '../services/api';
import './MapSourceAuditDropdown.css';

let compactSummaryCache = null;
let compactSummaryRequest = null;

export function resetMapSourceAuditCache() {
  compactSummaryCache = null;
  compactSummaryRequest = null;
}

function loadCompactSummary() {
  if (compactSummaryCache) return Promise.resolve(compactSummaryCache);
  if (!compactSummaryRequest) {
    compactSummaryRequest = apiJson('/research/data-warehouse-audit?compact=1')
      .then((payload) => {
        compactSummaryCache = payload;
        return payload;
      })
      .catch((error) => {
        compactSummaryRequest = null;
        throw error;
      });
  }
  return compactSummaryRequest;
}

function formatDate(value, includeTime = false) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-US', includeTime
    ? { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }
    : { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatNumber(value) {
  if (value == null) return '—';
  return new Intl.NumberFormat('en-US').format(Number(value));
}

function formatPeriodRange(earliest, latest) {
  if (!latest) return '—';
  return earliest ? `${formatDate(earliest)}–${formatDate(latest)}` : formatDate(latest);
}

function simpleStatus(dataset) {
  if (['import_failed', 'missing_regions', 'needs_review'].includes(dataset.pipelineStatus)) return { label: 'Needs Attention', tone: 'needs_review' };
  if (dataset.pipelineStatus === 'running') return { label: 'Running', tone: 'running' };
  if (dataset.pipelineStatus === 'pending') return { label: 'Pending', tone: 'pending' };
  if (dataset.pipelineStatus === 'disabled') return { label: 'Disabled', tone: 'disabled' };
  return { label: 'OK', tone: 'pass' };
}

export function datasetNeedsAttention(dataset) {
  const pipelineStatus = String(dataset?.pipelineStatus || '').toLowerCase();
  const freshness = String(dataset?.sourceFreshness || '').toLowerCase();
  return (
    ['import_failed', 'missing_regions', 'needs_review', 'pending', 'disabled', 'failed', 'error'].includes(pipelineStatus)
    || ['stale', 'pending', 'error'].includes(freshness)
    || Number(dataset?.regionsMissing || 0) > 0
  );
}

function statusContext(dataset, status) {
  const runDate = dataset.latestRunFinishedAt || dataset.latestRunImportedAt;
  if (status.label === 'OK') return runDate ? `Successful run: ${formatDate(runDate)}` : 'Latest run successful';
  if (status.label === 'Needs Attention') return Number(dataset.regionsMissing || 0) > 0 ? `${formatNumber(dataset.regionsMissing)} regions affected` : 'Latest run needs review';
  if (status.label === 'Running') return dataset.latestRunStartedAt ? `Started: ${formatDate(dataset.latestRunStartedAt)}` : 'Run in progress';
  if (status.label === 'Pending') return 'No completed run yet';
  if (status.label === 'Disabled') return 'Monitoring disabled';
  return '';
}

function scheduleLabel(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'daily metadata poll; conditional release extraction') return 'Daily check; pull on change';
  if (normalized === 'manual review when the workbook changes') return 'Check on source change';
  if (normalized === 'manual until weekly activation') return 'Manual';
  return value || 'Schedule not set';
}

function statusTone(value) {
  const status = String(value || '').toLowerCase();
  if (['pass', 'fresh', 'completed', 'loaded', 'no_change'].includes(status)) return 'good';
  if (['failed', 'fail', 'error', 'import_failed', 'disabled', 'low'].includes(status)) return 'bad';
  if (['stale', 'warn', 'needs_review', 'missing_regions', 'pending', 'medium_high', 'medium-high', 'medium'].includes(status)) return 'warn';
  if (['completed_with_errors', 'historical_baseline', 'lagged', 'running'].includes(status)) return 'info';
  if (['high', 'good'].includes(status)) return 'good';
  return 'neutral';
}

function trustStatus(dataset) {
  const label = String(dataset?.reliabilityLabel || '').toLowerCase();
  if (label.includes('high')) return 'high';
  if (label.includes('medium')) return 'medium';
  if (label.includes('low')) return 'low';
  return dataset?.reliabilityTier;
}

function Badge({ children, status }) {
  return <span className={`map-source-audit__badge map-source-audit__badge--${statusTone(status)}`}>{children}</span>;
}

function regionsStatLabel(dataset) {
  return dataset.regionsRequested != null ? 'Regions loaded / missing' : 'Regions loaded';
}

function regionsStatValue(dataset) {
  if (dataset.regionsLoaded == null) return '—';
  if (dataset.regionsRequested == null) return formatNumber(dataset.regionsLoaded);
  return `${formatNumber(dataset.regionsLoaded)} / ${formatNumber(dataset.regionsMissing)}`;
}

function ColumnHeader({ label, expanded, onToggle }) {
  return (
    <button type="button" className="map-source-audit__column-toggle" aria-expanded={expanded} onClick={onToggle}>
      {label}<span aria-hidden="true">⌄</span>
    </button>
  );
}

export default function MapSourceAuditDropdown({ datasetKeys }) {
  const datasetKeySignature = datasetKeys.join('|');
  const detailCacheRef = useRef({});
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedKey, setSelectedKey] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    loadCompactSummary()
      .then((payload) => {
        if (!active) return;
        const allowed = new Set(datasetKeySignature.split('|'));
        setDatasets((payload?.datasets || []).filter((dataset) => allowed.has(dataset.datasetKey)));
      })
      .catch((loadError) => {
        if (!active) return;
        if (isUnauthorizedError(loadError)) {
          window.location.assign('/login');
          return;
        }
        setError(loadError?.message || 'Could not load source audit data.');
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [datasetKeySignature]);

  async function openDetail(datasetKey) {
    const summary = datasets.find((dataset) => dataset.datasetKey === datasetKey);
    const cached = detailCacheRef.current[datasetKey];
    setSelectedKey(datasetKey);
    setDetail(cached || (summary ? {
      dataset: summary,
      source: {
        sourceUrl: summary.sourceUrl,
        documentationUrl: summary.documentationUrl,
        pythonFile: summary.pythonFile,
        cronCommand: summary.cronCommand,
      },
      recentRuns: [],
      recentChanges: summary.datasetKey === 'zillow_market_snapshot' ? [] : undefined,
    } : null));
    setDetailError('');
    if (cached) {
      setDetailLoading(false);
      return;
    }
    setDetailLoading(true);
    try {
      const payload = await apiJson(`/research/data-warehouse-audit/${encodeURIComponent(datasetKey)}?compact=1`);
      if (summary && payload?.dataset) {
        payload.dataset = { ...payload.dataset, ...summary };
      }
      detailCacheRef.current[datasetKey] = payload;
      setDetail(payload);
    } catch (loadError) {
      if (isUnauthorizedError(loadError)) {
        window.location.assign('/login');
        return;
      }
      setDetailError(loadError?.message || 'Could not load dataset detail.');
    } finally {
      setDetailLoading(false);
    }
  }

  function closeDetail() {
    setSelectedKey('');
    setDetail(null);
    setDetailError('');
  }

  const detailDataset = detail?.dataset;
  const source = detail?.source || {};
  const hasSourceChangeHistory = Array.isArray(detail?.recentChanges);
  const compactRunHistory = hasSourceChangeHistory ? detail.recentChanges : (detail?.recentRuns || []).slice(0, 5);
  const requestedDatasetCount = new Set(datasetKeys).size;
  const missingRequestedDataset = !loading && !error && datasets.length < requestedDatasetCount;
  const needsAttention = Boolean(error) || missingRequestedDataset || datasets.some(datasetNeedsAttention);
  const attentionRequiresOpen = !loading && needsAttention;
  const auditExpanded = expanded || attentionRequiresOpen;
  const toggleExpanded = () => {
    if (!attentionRequiresOpen) setExpanded((current) => !current);
  };
  const health = needsAttention ? 'attention' : 'healthy';

  return (
    <div className="map-source-audit">
      <div className={`map-source-audit__panel map-source-audit__panel--${health}${auditExpanded ? ' is-expanded' : ''}`} aria-label="Map data source audit" data-health={health}>
        {error ? <div className="map-source-audit__state map-source-audit__state--error">{error}</div> : null}
        {!error ? (
          <table>
            <thead><tr>{['Dataset', 'Status', 'Data Through', 'Last Run', 'Last Changed', 'Freshness', 'Trust', 'Details'].map((label) => <th key={label}><ColumnHeader label={label} expanded={auditExpanded} onToggle={toggleExpanded} /></th>)}</tr></thead>
            {auditExpanded ? <tbody>
              {loading ? <tr><td colSpan="8" className="map-source-audit__state">Loading audit details…</td></tr> : null}
              {datasets.map((dataset) => {
                const status = simpleStatus(dataset);
                return (
                  <tr key={dataset.datasetKey}>
                    <td><button type="button" onClick={() => openDetail(dataset.datasetKey)}>{dataset.name}</button><small>{dataset.sourceName} · {dataset.geographyLevel || 'geography not set'}</small></td>
                    <td><Badge status={status.tone}>{status.label}</Badge><small>{statusContext(dataset, status)}</small></td>
                    <td>{formatPeriodRange(dataset.earliestSourcePeriod, dataset.latestSourcePeriod)}{dataset.pulledThroughPeriod && dataset.pulledThroughPeriod !== dataset.latestSourcePeriod ? <small>Pulled through {formatDate(dataset.pulledThroughPeriod)}</small> : null}</td>
                    <td>{formatDate(dataset.latestRunFinishedAt || dataset.latestRunImportedAt, true)}<small>{scheduleLabel(dataset.recommendedPullFrequency || dataset.updateFrequency)}</small></td>
                    <td>{formatDate(dataset.lastChangedAt, true)}<small>{dataset.lastChangedAt ? 'Data changed successfully' : 'No recorded change yet'}</small></td>
                    <td><Badge status={dataset.sourceFreshness}>{dataset.sourceFreshnessLabel || dataset.sourceFreshness || 'Unknown'}</Badge></td>
                    <td><Badge status={trustStatus(dataset)}>{String(dataset.reliabilityLabel || 'Unscored').replace(/\s+trust$/i, '')}</Badge></td>
                    <td><button type="button" className="map-source-audit__open" aria-label={`More information about ${dataset.name}`} title="More information" onClick={() => openDetail(dataset.datasetKey)}><svg aria-hidden="true" viewBox="0 0 16 16"><circle cx="8" cy="8" r="6.25" /><path d="M8 7v4" /><circle cx="8" cy="4.75" r=".6" className="map-source-audit__info-dot" /></svg></button></td>
                  </tr>
                );
              })}
              {!loading && !datasets.length ? <tr><td colSpan="8">No matching audit records are available.</td></tr> : null}
            </tbody> : null}
          </table>
        ) : null}
      </div>

      {selectedKey ? (
        <div className="map-source-audit__drawer-layer" role="presentation" onClick={closeDetail}>
          <aside className="map-source-audit__drawer" role="dialog" aria-modal="true" aria-label="Dataset audit detail" onClick={(event) => event.stopPropagation()}>
            <header>
              <div><p>Dataset detail</p><h2>{detailDataset?.name || selectedKey}</h2><span>{detailDataset?.sourceName || 'Loading source detail…'}</span></div>
              <button type="button" aria-label="Close dataset detail" onClick={closeDetail}><i className="pi pi-times" /></button>
            </header>
            {detailLoading ? <div className="map-source-audit__drawer-state">Loading dataset detail…</div> : null}
            {detailError ? <div className="map-source-audit__drawer-state map-source-audit__state--error">{detailError}</div> : null}
            {detailDataset ? (
              <div className="map-source-audit__drawer-body">
                <div className="map-source-audit__drawer-badges">
                  <Badge status={detailDataset.pipelineStatus}>{detailDataset.pipelineStatusLabel || detailDataset.pipelineStatus}</Badge>
                  <Badge status={detailDataset.sourceFreshness}>{detailDataset.sourceFreshnessLabel || detailDataset.sourceFreshness}</Badge>
                  <Badge status={trustStatus(detailDataset)}>{detailDataset.reliabilityLabel || detailDataset.reliabilityTier}</Badge>
                </div>
                <section className="map-source-audit__stats">
                  <div><span>Last successful run</span><strong>{formatDate(detailDataset.lastSuccessfulRunAt, true)}</strong></div>
                  <div><span>Last successful change</span><strong>{formatDate(detailDataset.lastChangedAt, true)}</strong></div>
                  <div><span>Latest source period</span><strong>{formatDate(detailDataset.latestSourcePeriod)}</strong></div>
                  <div><span>Rows pulled / written</span><strong>{detailDataset.rowsPulled ?? '—'} / {detailDataset.rowsWritten ?? '—'}</strong></div>
                  <div><span>{regionsStatLabel(detailDataset)}</span><strong>{regionsStatValue(detailDataset)}</strong></div>
                </section>
                <section className="map-source-audit__detail-section">
                  <h3>Source & job</h3>
                  <dl>
                    <div><dt>Source URL</dt><dd>{source.sourceUrl ? <a href={source.sourceUrl} target="_blank" rel="noreferrer">{source.sourceUrl}</a> : '—'}</dd></div>
                    {source.documentationUrl && source.documentationUrl !== source.sourceUrl ? (
                      <div><dt>Documentation</dt><dd><a href={source.documentationUrl} target="_blank" rel="noreferrer">{source.documentationUrl}</a></dd></div>
                    ) : null}
                    <div><dt>Script / job</dt><dd>{source.pythonFile || '—'}</dd></div>
                    <div><dt>Schedule</dt><dd>{source.cronCommand || detailDataset.recommendedPullFrequency || '—'}</dd></div>
                  </dl>
                </section>
                <section className="map-source-audit__detail-section">
                  <h3>{hasSourceChangeHistory ? 'Recent source changes' : 'Recent runs'}</h3>
                  {hasSourceChangeHistory ? <p className="map-source-audit__change-note">A period may repeat when Zillow republishes or revises its files.</p> : null}
                  {!hasSourceChangeHistory ? <p className="map-source-audit__change-note">Skipped usually means no new source release was available; zero rows is expected.</p> : null}
                  {compactRunHistory.map((run) => (
                    <div className="map-source-audit__run" key={run.runId}>
                      <Badge status={run.status}>{run.statusLabel || run.status}</Badge>
                      <span>{formatDate(run.finishedAt || run.startedAt, true)}</span>
                      <strong>{hasSourceChangeHistory ? `${formatDate(run.latestSourcePeriod)} source period` : `${formatNumber(run.rowsWritten ?? 0)} rows written`}</strong>
                    </div>
                  ))}
                  {!compactRunHistory.length && detailLoading ? <p>Loading recent audit history…</p> : null}
                  {!compactRunHistory.length && !detailLoading ? <p>No source changes recorded in the available audit history.</p> : null}
                </section>
              </div>
            ) : null}
          </aside>
        </div>
      ) : null}
    </div>
  );
}
